import { v4 as uuidv4 } from "uuid";

// Spacing constants for node positioning - use these across all canvas operations
export const NODE_SPACING = 320;      // Horizontal gap for textInput/step nodes
export const CONNECTOR_SPACING = 200; // Horizontal gap for circle/connector nodes
export const SHIFT_STEP = 317;        // Shift amount when deleting a step node
export const SHIFT_CIRCLE = 110;      // Shift amount when deleting a circle node
export const BRANCH_Y_GAP = 110;      // Vertical gap between stacked parallel branches

const actionNodes = ['notification', 'estimation', 'httpRequest', 'sharePublicForm', 'duplicateCheck', 'outlookCalendar', 'googlePlaces']

export const createNode = (type, previousNode, connectorNode, prevEdge) => {
    // Default new nodes are draggable unless explicitly turned off (e.g. for plus/connector nodes)
    let newNode = {  id: uuidv4(), draggable: true, position: { x: "", y: ""}, data: {}};
    
    // Intermediate phase
    if (type === 'step' || type === "approvalStep") {
        newNode.data.name = type === "step" ? "Workflow Step" : "Approval Step"
        newNode.type = "textInput"
        newNode.position.x = getXPosition(type, connectorNode);
        newNode.position.y = getYPosition("plainStep", previousNode, connectorNode)
    }     

    // notification-like & verification nodes as compact diamond connectors
    else if (actionNodes.includes(type)) {
        newNode.type = "circle"
        newNode.data = { type: type }
        newNode.position.x = getXPosition(type, connectorNode);
        newNode.position.y = getYPosition("notificationNode", previousNode, connectorNode)
    }
    
    // connector node except for condition (+ button)
    else if (type === "connector") {
        newNode.type = "circle"
        newNode.position.x = getXPosition(type, previousNode)
        newNode.position.y = getYPosition("connector", previousNode, "N")
        newNode.draggable = false // keep connector (+) fixed

    } 
    else if (type === "notificationConnector") {
        newNode.type = "circle"
        newNode.position.x = getXPosition(type, previousNode)
        newNode.position.y = getYPosition("notificationConnector", previousNode, "N")
    }
    // end node
    else if (type === "end") {
        newNode.type = "circle"
        newNode.data = { type: type, name: "Approved" }
        newNode.position.x = getXPosition(type, connectorNode)
        if(prevEdge && prevEdge.sourceHandle === "conditionb") {
            newNode.position.y = getYPosition("notificationFromConditionNo", previousNode, connectorNode)
        } else {
            newNode.position.y = getYPosition("notificationNode", previousNode, connectorNode)
        }
    } 

    else if (type === "approve" || type === "reject") {
        newNode.type = "output"
        newNode.data = { type: type, name: type + "d" }
        newNode.position.x = previousNode.position.x + 130
        if(type === "approve") {
            newNode.position.y = previousNode.position.y - 18
        } else {
            newNode.position.y = previousNode.position.y + 110

        }
    } else if (type === "approveConnector" || type === "rejectConnector") {
        newNode.type = "circle"
        newNode.draggable = false
        newNode.data.type = "endConnector"
        newNode.position.x = previousNode.position.x + 260
        newNode.position.y = previousNode.position.y + 33
    }
    // Do not force draggable false globally – respect per-node setting above
    return { ...newNode }

}

// Factory for the merge (join) node that terminates a parallel region.
// Position is supplied by the caller (computed from branch geometry).
export const createJoinNode = (parallelGroupId, position) => ({
    id: uuidv4(),
    type: "circle",
    draggable: true,
    position: { ...position },
    data: {
        type: "join",
        name: "Merge",
        parallelGroupId,
        joinRule: { branches: [], optionalGroups: [] }
    }
})


const getXPosition = (type, previousNode) => {
    let newNodeXPosition = previousNode.position.x;

    if (type === "connector") {
        newNodeXPosition = newNodeXPosition + 317;
    } else if (type === "condition" || type === "notification" || type === "httpRequest") {
        // keep same X for side nodes like condition/notification/httpRequest
    } else if (type === "conditionConnectorYes" || type === "conditionConnectorNo") {
        newNodeXPosition = newNodeXPosition + 230;
    } else if(type === "stepFromConditionNo") {
        newNodeXPosition = newNodeXPosition + 360;
    } else if (type === "notificationConnector") {
        newNodeXPosition = newNodeXPosition  + 110
    }
    return newNodeXPosition;
}

const getYPosition = (type, prevNode, connectorNode={}) => {
    let newNodeYPosition = "";
    let prevNodeType = previousStepType(prevNode)
    switch(type) {
        case "plainStep": 
            newNodeYPosition = connectorNode.position.y + fixYPositionForNodes[prevNodeType]["textInput"] //73;
            break;
        case "stepFromConditionNo": 
            newNodeYPosition = connectorNode?.position.y + fixYPositionForNodes[prevNodeType]["textInput"];
            break;
        case "connector": 
            newNodeYPosition = prevNode.position.y + fixYPositionForNodes[prevNodeType]["connector"]
            break;
        case "notificationConnector": 
            newNodeYPosition = prevNode.position.y + fixYPositionForNodes[prevNodeType]["notificationConnector"]
            break;
        case "notificationNode": 
            newNodeYPosition = prevNode.position.y + fixYPositionForNodes[prevNodeType]["notificationNode"]  //153
            break;
        case "conditionNode": 
            newNodeYPosition = prevNode.position.y + fixYPositionForNodes[prevNodeType]["conditionNode"]  //153
            break;
        case "conditionConnectorYes":   
            newNodeYPosition = prevNode.position.y + fixYPositionForNodes[prevNodeType]["conditionConnectorYes"]
            break;
        case "conditionConnectorNo": 
            newNodeYPosition = prevNode.position.y + fixYPositionForNodes[prevNodeType]["conditionConnectorNo"]
            break;

        case "notificationFromConditionNo":
            newNodeYPosition = connectorNode?.position.y + fixYPositionForNodes[prevNodeType]["notificationFromConditionNo"]
            break;

        default:
            newNodeYPosition = 40
        
    }

    // if (type === 'step') {
    //     newNodeYPosition = 73;
    // } else if (type === "connector") {
    //     newNodeYPosition = 164
    // } else if (type === "condition") {
    //     newNodeYPosition = 153
    // } else if (type === "conditionConnector") {
    //     if(conditionPass === "Y") {
    //         newNodeYPosition = 167
    //     } else {
    //         newNodeYPosition = 170 + 250
    //     }
    // }
    return newNodeYPosition
}


export const createEdge = (type, previousNode, nextNode, conditionPass="Y", prevCurrentEdge) => {
    let newEdge = { id: uuidv4(), source: previousNode.id, target: nextNode.id }

    if(type === "step" || type === "notification" || type === "httpRequest" || type === "sharePublicForm" || type === "outlookCalendar" || type === "googlePlaces") {
        newEdge.type = "figma" // Use Figma-style edges
        if(prevCurrentEdge?.sourceHandle) {
            newEdge.sourceHandle = prevCurrentEdge.sourceHandle
        }
    } else if (type === "condition") {
        if(conditionPass === "Y") {
            newEdge.type = "figma" // Use Figma-style edges
            newEdge.sourceHandle = "conditiona"
        } else {
            newEdge.type = "figma" // Use Figma-style edges for smooth curves
            newEdge.sourceHandle = "conditionb"
        }
        newEdge.label = conditionPass === "Y" ? "If Yes" : "If No"

    } else if (type === "approve" || type === "reject") {
        newEdge.type = type === "approve" ? "figma" : "figma" // Both use Figma style
        newEdge.sourceHandle = type
    }

    return newEdge

}

const previousStepType = (previousNode) => {
    return previousNode.type
}


const fixYPositionForNodes = {
    "default": {
        "textInput": 0,
        "conditionNode": 52,
        "conditionYes": 153,
        "conditionNo": 250,
        "notificationNode": 0,
        "connector": 0,
        "notificationConnector": 0
    },
    "textInput": {
        "default": 0,
        "textInput": 0,
        "notification": 151,
        "circle": 110,
        "connector": 0,
        "notificationNode": 0
    },
    "connector": {
        "textInput": 0,
        "conditionYes": 167,
        "conditionNo": 250
    },
    "circle": {
        "conditionConnectorNo": 280,
        "conditionConnectorYes": 15,
        "textInput": 0,
        "stepFromConditionNo": -91,
        "connector": 0,
        "notificationConnector": 0,
        "conditionNode":  0,
        "notificationNode": 0,
        "notificationFromConditionNo": 0

    }
}


export const defaultMenuItems = [
    { label: 'Step', value: 'step' },
    // { label: 'Flow Condition', value: 'condition' },
    // { label: 'Estimation', value: 'estimation' },
    // { label: 'Notification', value: 'notification' },
    // { label: 'Merge Flow', value: 'mergeFlow' },
    { label: 'HTTP Request', value: 'httpRequest' },
    { label: 'End', value: 'end' }
]

export const getNodeXPosition = (currentNode, side) => {
    let reqPosition = currentNode.position.x 
    if(side === "right") {
        if(currentNode.type === "circle") {
            reqPosition += CONNECTOR_SPACING
        } else {
            reqPosition += NODE_SPACING
        }
    }
    return reqPosition
}

export const getNodeYPosition = (currentNode) => {
    let reqPosition = currentNode.position.y
    return reqPosition
}

export const getNodeEdgeToBeDeleted = (currentNode, edges, nodes) => {
    let reqNodesEdges = {nodes: [currentNode.id], edges: [], lastNode: ""}
    let nextEdges = edges.filter(edge => edge.source === currentNode.id)

    // if(currentNode?.data?.type === "end") {
    //     let endNodePrevEdge = edges.find(edge => edge.target === currentNode.id)
    //     if(endNodePrevEdge){
    //         reqNodesEdges.lastNode = endNodePrevEdge.source
    //     }
    // }
    if(nextEdges.length > 0) {
        nextEdges.forEach(edge => {
            reqNodesEdges.edges = reqNodesEdges.edges.concat(edge.id);
            let targetNode = nodes.find(node => node.id === edge.target);
            if(targetNode) {
                reqNodesEdges.nodes = reqNodesEdges.nodes.concat(targetNode.id);
                let nextEdge = edges.find(lastEdge => lastEdge.source === targetNode.id);
                if(nextEdge){
                    let lastNode = nodes.find(lNode => lNode.id === nextEdge.target)
                    if(!reqNodesEdges.lastNode) {
                        reqNodesEdges.lastNode = lastNode.id
                    } else {
                        reqNodesEdges.edges = reqNodesEdges.edges.concat(nextEdge.id);
                        reqNodesEdges.nodes = reqNodesEdges.nodes.concat(lastNode.id);

                    }
                }
            }
        })
    }
    return reqNodesEdges
}

export const sortNodesIndexWise = (nodes) => {
    let updatedNodes = [...nodes]
    updatedNodes.sort((a,b) => a.position.x - b.position.x)
    return updatedNodes
}
